import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePocketBaseRealtime } from '../hooks/usePocketBaseRealtime';
import { pb, getRestaurantId } from '../config/pocketbase';
import { supabase } from '../config/supabase';

const { FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiSettings, FiChevronDown, FiChevronUp, FiImage, FiEyeOff } = FiIcons;

const Backoffice = () => {
  const { data: menu = [], loading } = usePocketBaseRealtime('menu');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    categoria: 'bevande',
    prezzo: '',
    disponibile: true,
    descrizione: '',
    immagini: []
  });

  const [expandedCategories, setExpandedCategories] = useState({
    bevande: true, primi: true, pizze: true, secondi: true, dolci: true
  });

  const categoriesOrder = ["bevande", "primi", "pizze", "secondi", "dolci"];
  const [groupedMenu, setGroupedMenu] = useState({});

  useEffect(() => {
    const grouped = menu.reduce((acc, item) => {
      const cat = categoriesOrder.includes(item.categoria) ? item.categoria : "bevande";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
    setGroupedMenu(grouped);
  }, [menu]);

  const resetForm = () => {
    setFormData({ nome: '', categoria: 'bevande', prezzo: '', disponibile: true, descrizione: '', immagini: [] });
    setNewImageUrl('');
    setEditingItem(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setFormData({
      nome: item.nome,
      categoria: item.categoria,
      prezzo: item.prezzo.toString(),
      disponibile: item.disponibile,
      descrizione: item.descrizione || '',
      immagini: Array.isArray(item.immagini) ? item.immagini : []
    });
    setEditingItem(item);
    setShowForm(true);
  };

  const handleAddImage = () => {
    if (newImageUrl && formData.immagini.length < 4) {
      setFormData(prev => ({ ...prev, immagini: [...prev.immagini, newImageUrl] }));
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({ ...prev, immagini: prev.immagini.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      nome: formData.nome,
      categoria: formData.categoria,
      prezzo: parseFloat(formData.prezzo),
      disponibile: formData.disponibile,
      descrizione: formData.descrizione || null,
      immagini: formData.immagini.length > 0 ? formData.immagini : null,
      ristorante_id: getRestaurantId()
    };

    try {
      if (editingItem) {
        try {
          await pb.collection('menu_tap2order').update(editingItem.id, data);
        } catch {
          await supabase.from('menu_tap2order').update(data).eq('id', editingItem.id);
        }
      } else {
        try {
          await pb.collection('menu_tap2order').create(data);
        } catch {
          await supabase.from('menu_tap2order').insert([data]);
        }
      }
      resetForm();
    } catch (error) {
      console.error('Errore salvataggio piatto:', error);
      alert('Errore salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Eliminare questo piatto?')) {
      try {
        await pb.collection('menu_tap2order').delete(id);
      } catch {
        await supabase.from('menu_tap2order').delete().eq('id', id);
      }
    }
  };

  if (loading) return <div className="p-8 text-center">Caricamento...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <SafeIcon icon={FiSettings} className="mr-3 text-[#5C715E]" /> Gestione Menù Completo
        </h1>
        <button onClick={() => setShowForm(true)} className="bg-[#5C715E] text-white px-6 py-3 rounded-lg flex items-center shadow-lg">
          <SafeIcon icon={FiPlus} className="mr-2" /> Nuovo Piatto
        </button>
      </div>

      <div className="grid gap-6">
        {categoriesOrder.map(cat => (
          <div key={cat} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-gray-50 flex justify-between items-center cursor-pointer" onClick={() => setExpandedCategories(p => ({...p, [cat]: !p[cat]}))}>
              <h2 className="font-bold text-lg uppercase tracking-wider">{cat}</h2>
              <SafeIcon icon={expandedCategories[cat] ? FiChevronUp : FiChevronDown} />
            </div>
            {expandedCategories[cat] && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(groupedMenu[cat] || []).map(item => (
                  <div key={item.id} className={`p-4 rounded-xl border-2 transition-all ${item.disponibile ? 'border-gray-100' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-gray-900">{item.nome}</h3>
                      <span className="font-bold text-[#5C715E]">€{item.prezzo.toFixed(2)}</span>
                    </div>
                    {!item.disponibile && (
                      <div className="flex items-center text-red-600 text-xs font-bold mb-3 uppercase">
                        <SafeIcon icon={FiEyeOff} className="mr-1" /> Nascosto al cliente
                      </div>
                    )}
                    <div className="flex justify-end space-x-2 mt-4">
                      <button onClick={() => handleEdit(item)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"><SafeIcon icon={FiEdit} /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><SafeIcon icon={FiTrash2} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">{editingItem ? 'Modifica' : 'Nuovo'} Piatto</h2>
                <button onClick={resetForm}><SafeIcon icon={FiX} className="w-6 h-6" /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" placeholder="Nome" value={formData.nome} onChange={e => setFormData(p => ({...p, nome: e.target.value}))} className="p-2 border rounded-lg" required />
                  <select value={formData.categoria} onChange={e => setFormData(p => ({...p, categoria: e.target.value}))} className="p-2 border rounded-lg">
                    {categoriesOrder.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input type="number" step="0.01" placeholder="Prezzo" value={formData.prezzo} onChange={e => setFormData(p => ({...p, prezzo: e.target.value}))} className="p-2 border rounded-lg" required />
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" checked={formData.disponibile} onChange={e => setFormData(p => ({...p, disponibile: e.target.checked}))} id="disp" />
                    <label htmlFor="disp" className="text-sm font-bold">Disponibile nel menù</label>
                  </div>
                </div>
                <textarea placeholder="Descrizione" value={formData.descrizione} onChange={e => setFormData(p => ({...p, descrizione: e.target.value}))} className="w-full p-2 border rounded-lg h-24" />
                
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="font-bold text-sm mb-2">Immagini (max 4)</p>
                  <div className="flex space-x-2 mb-4">
                    <input type="url" value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} placeholder="URL Immagine" className="flex-1 p-2 border rounded-lg text-sm" />
                    <button type="button" onClick={handleAddImage} className="bg-[#5C715E] text-white px-4 rounded-lg">Aggiungi</button>
                  </div>
                  <div className="flex space-x-2">
                    {formData.immagini.map((img, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                        <img src={img} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-red-600 text-white rounded-bl-lg p-0.5"><SafeIcon icon={FiX} className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button type="button" onClick={resetForm} className="flex-1 py-3 border rounded-xl">Annulla</button>
                  <button type="submit" className="flex-1 py-3 bg-[#5C715E] text-white rounded-xl font-bold">Salva</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Backoffice;